/**
 * Screen 1 of the self-service onboarding flow: "Create your account".
 *
 * Rendered inside AuthLayout (same shell as the login screen). Each provider
 * button runs the start → OAuth sequence: the Turnstile token (when captcha
 * is configured) is POSTed to `/self-service/start/` to arm the session, the
 * wizard's first step is stashed as the post-OAuth return URL (consumed by
 * `ProviderCallback`), and the browser is handed to allauth's provider login.
 */
import { useCallback, useState } from 'react';

import { AuthProcess } from '@/lib/allauth';
import { getProviderButtonLabel } from '@/lib/auth/provider-labels';
import { HintText } from '@/lib/base/forms';
import {
  useSelfServiceStatus,
  useStartSelfServiceSignup,
} from '@/lib/self-service/api';
import { TurnstileWidget } from '@/lib/self-service/components/TurnstileWidget';
import { FIRST_STEP_PATH } from '@/lib/self-service/constants';
import ProviderList from '@/pages/auth/ProviderList';

const OAUTH_CALLBACK_PATH = '/account/provider/callback';
/** sessionStorage key `ProviderCallback` reads to decide where to land after OAuth. */
const OAUTH_RETURN_URL_KEY = 'oauth_return_url';

export function SelfServiceSignupContent() {
  const { data: status } = useSelfServiceStatus();
  const siteKey = status?.turnstile_site_key ?? null;
  const start = useStartSelfServiceSignup();
  const [turnstileToken, setTurnstileToken] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const { mutateAsync: startSignup } = start;
  const beforeRedirect = useCallback(async () => {
    setError(null);
    await startSignup(turnstileToken);
    try {
      sessionStorage.setItem(OAUTH_RETURN_URL_KEY, FIRST_STEP_PATH);
    } catch {
      // Storage unavailable — ProviderCallback falls back to the default landing.
    }
  }, [startSignup, turnstileToken]);

  const needsToken = siteKey !== null && turnstileToken === null;

  return (
    <div className='flex flex-col gap-8'>
      {/* Header */}
      <div className='flex flex-col gap-3'>
        <h1 className='font-display text-4xl font-medium text-brand-600 dark:text-brand-400'>
          Create your account
        </h1>
        <p className='text-base text-[var(--color-text-tertiary)]'>
          Ahoy starts working before you've finished setting up. Try it out for
          14 days free on the Startup plan.
        </p>
      </div>

      {siteKey !== null && (
        <TurnstileWidget siteKey={siteKey} onToken={setTurnstileToken} />
      )}

      <div className='flex flex-col gap-3'>
        <ProviderList
          callbackURL={OAUTH_CALLBACK_PATH}
          process={AuthProcess.LOGIN}
          getLabel={getProviderButtonLabel}
          isDisabled={needsToken}
          beforeRedirect={beforeRedirect}
          onError={setError}
        />
        {error && (
          <div role='alert'>
            <HintText isInvalid showError>
              {error}
            </HintText>
          </div>
        )}
      </div>

      {/* Trial fine print */}
      <p className='text-sm font-medium text-[var(--color-text-tertiary)]'>
        A card's needed to start your trial – no charge for 14 days. After that,
        $99 per seat/month or $79 billed annually. Cancel any time.
      </p>
    </div>
  );
}
